<script setup lang="ts">
  defineOptions({ name: 'CPTag', inheritAttrs: false })

  import type { PropType } from 'vue'

  import { HLTag } from '@platform-ui/highrise'
  import type { HLTagColor, HLTagSize } from '@platform-ui/highrise'

  const props = defineProps({
    id: {
      type: String,
      default: undefined,
    },
    size: {
      type: String as PropType<HLTagSize>,
      default: undefined,
    },
    color: {
      type: String as PropType<HLTagColor>,
      default: undefined,
    },
    bordered: {
      type: Boolean,
      default: undefined,
    },
    round: {
      type: Boolean,
      default: undefined,
    },
    closable: {
      type: Boolean,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: undefined,
    },
    interactive: {
      type: Boolean,
      default: undefined,
    },
    truncate: {
      type: Boolean,
      default: undefined,
    },
    count: {
      type: Number,
      default: undefined,
    },
    maxWidth: {
      type: [String, Number],
      default: undefined,
    },
  })
</script>

<template>
  <HLTag
    v-bind="$attrs"
    :id="props.id"
    class="cp-tag"
    :size="props.size"
    :color="props.color"
    :bordered="props.bordered"
    :round="props.round"
    :closable="props.closable"
    :disabled="props.disabled"
    :interactive="props.interactive"
    :truncate="props.truncate"
    :count="props.count"
    :max-width="props.maxWidth"
  >
    <template v-if="$slots.icon || $slots.suffix" #icon>
      <slot name="icon" />
      <slot name="suffix" />
    </template>
    <template v-if="$slots.avatar" #avatar>
      <slot name="avatar" />
    </template>
    <slot />
  </HLTag>
</template>

<style>
  /* Global by design: bridge the Highrise gray modifier at the CP wrapper boundary.
     Highrise sets the --n-* variables inline, so those overrides require !important. */
  .cp-tag.hr-tag--gray {
    --n-color: var(--cp-surface-500) !important;
    --n-text-color: var(--cp-font-700) !important;
    --gray-600: var(--cp-font-700);
    --gray-900: var(--cp-font-700);
  }

  .cp-tag.hr-tag--gray.hr-tag--bordered {
    --n-border: 1px solid var(--cp-border-500) !important;
  }

  .cp-tag .hr-tag__count,
  .cp-tag .n-tag__count {
    background-color: var(--cp-surface-600) !important;
    color: var(--cp-font-700) !important;
    border-color: var(--cp-border-500) !important;
  }
</style>
